import { z } from "zod";
import { clinicalPhaseSchema } from "./clinical-phase";
import { weeklyFrequencySchema } from "./dose-interval";
import { orderCatalogueFields } from "./medication-catalogue";
import { PRESCRIPTION_NOT_AT_DECISION } from "./prescription-choice";
import {
  renewalSymptomsSchema,
  type SymptomSafetyLevel,
} from "./symptom-entry";

export const prescriptionCycleStateSchema = z.enum([
  "pending_assessment",
  "active",
  "expiring_soon",
  "renewal_pending",
  "doctor_review",
  "approved",
  "rejected",
  "paused",
  "discontinued",
]);
export type PrescriptionCycleState = z.infer<
  typeof prescriptionCycleStateSchema
>;

// Allowed transitions. Source of truth for both backend enforcement and frontend optimistic UI.
export const cycleTransitions: Record<
  PrescriptionCycleState,
  PrescriptionCycleState[]
> = {
  pending_assessment: ["active", "rejected", "discontinued"],
  active: ["expiring_soon", "paused", "discontinued"],
  expiring_soon: ["renewal_pending", "active", "discontinued"], // 'active' = grace extension
  renewal_pending: ["doctor_review", "discontinued"],
  doctor_review: ["approved", "rejected", "renewal_pending"], // last = doctor wants more info
  approved: [], // terminal — spawns next cycle
  rejected: [], // terminal
  paused: ["active", "discontinued"],
  discontinued: [],
};

export function canTransition(
  from: PrescriptionCycleState,
  to: PrescriptionCycleState,
): boolean {
  return cycleTransitions[from].includes(to);
}

// TES-636 — ONE prescription shape, the same everywhere a dose is set.
//
// The background: the first dose was never set by a doctor. The admin button
// sent the string "0.25mg" hardcoded (`PatientDetail.tsx`), and the server had
// the same default in two more places — so the number the patient read as her
// prescription was a constant in a React component. Dose adjustment and
// renewal, in turn, took free text with no structure.
//
// Hence this: medication, dose and frequency, all three REQUIRED, shared by
// the treatment decision, the dose adjustment and the renewal approval. An
// incomplete prescription must be refused, not guessed — that is the whole
// point, and every `|| "0.25mg"` is gone.
//
// The free text itself is still the doctor's (Ö-64: no parsing). What changes
// is that it now has three named parts instead of one string, and that none of
// them may be missing.
// TES-702 — the dose's FORM is validated; its content is not.
//
// TES-636 made the dose its own field but left it as free text, and everything
// was then accepted: `0.25` with no unit, `Wegovy 0.5` with the medication
// named a second time, `025mg`. The patient reads the string verbatim as her
// prescribed dose under the heading "Recept och läkemedel" — a missing unit is
// not a cosmetic fault there.
//
// The rule is deliberately narrow: ONE number and ONE unit. It says nothing
// about which dose is reasonable for which medication — that is a clinical
// question and it belongs with the doctor, not in a regular expression. Ö-64
// still holds: no parsing, no normalisation, the value is stored exactly as it
// was written.
//
//   accepted:  "0,25 mg"  "0.25mg"  "1 ml"  "12 E"  "2,5 MG"
//   rejected:  "0.25" (no unit)   "Wegovy 0.5" (medication name)
//              "025mg" (leading zero)   "0,25 mg/vecka" (frequency in the dose)
//
// A leading zero is rejected because "025mg" has no unambiguous reading, and a
// dose that can be read two ways is exactly the kind of fault this exists to
// catch.
const DOSE_FORM = /^(0|[1-9]\d{0,3})([.,]\d{1,3})?\s?(mg|mcg|µg|ml|E|IE)$/i;

// TES-1031 — what the dose field says when it refuses, in the doctor's words.
// The run-through found a prefilled "0.25" refused with a sentence about form
// that did not say the unit was the missing part; each case now names it.
export const DOSE_REQUIRED_MESSAGE = "Ange dos med enhet, t.ex. 0,25 mg.";
export const DOSE_UNIT_MISSING_MESSAGE =
  "Dosen saknar enhet. Ange dos med enhet, t.ex. 0,25 mg.";
export const DOSE_FORM_MESSAGE =
  "Ange dos som ett tal och en enhet (mg, mcg, µg, ml, E eller IE), t.ex. 0,25 mg. Läkemedel och frekvens har egna fält.";

const BARE_NUMBER = /^\d+([.,]\d+)?$/;

/**
 * Why a dose cannot be sent, or null when it passes the contract. The one
 * source of the dose field's error text, on the server and in admin alike.
 */
export function doseProblem(dose: string | null | undefined): string | null {
  const d = (dose ?? "").trim();
  if (!d) return DOSE_REQUIRED_MESSAGE;
  if (DOSE_FORM.test(d) && d.length <= 64) return null;
  if (BARE_NUMBER.test(d)) return DOSE_UNIT_MISSING_MESSAGE;
  return DOSE_FORM_MESSAGE;
}

// Spellings of micrograms that notes use but DOSE_FORM does not take. They
// name the SAME unit, so writing them as "µg" changes the spelling only.
const MICROGRAM_ALIAS = /^(\d+(?:[.,]\d+)?)\s*(mikrogram|ug)$/i;

/**
 * TES-1031 — a dose that may be PREFILLED into an approval, or "".
 *
 * A prefill that the form then refuses is worse than none: the doctor sees a
 * value she did not write, presses publish, and gets a refusal about it. So a
 * prefilled dose is either one the contract accepts or left empty for her.
 *
 * The one rewrite is the unit's spelling (micrograms written as "mikrogram" or
 * "ug" become "µg"). A unit is NEVER added: "0.25" says nothing about whether
 * milligrams or millilitres were meant, and guessing the unit of a dose is
 * exactly what the contract exists to stop.
 */
export function prefillDose(dose: string | null | undefined): string {
  const d = (dose ?? "").trim().replace(/\s+/g, " ");
  if (!d) return "";
  if (doseProblem(d) === null) return d;
  const alias = MICROGRAM_ALIAS.exec(d);
  if (alias) {
    const candidate = `${alias[1]} µg`;
    if (doseProblem(candidate) === null) return candidate;
  }
  return "";
}

export const prescriptionOrderSchema = z.object({
  /** The medication's name, e.g. "Wegovy". */
  medication: z.string().trim().min(1).max(120),
  /** The strength, exactly as the doctor writes it, e.g. "0,25 mg". */
  dose: z
    .string()
    .trim()
    .min(1, DOSE_REQUIRED_MESSAGE)
    .max(64)
    .refine(
      (v) => DOSE_FORM.test(v),
      (v) => ({ message: doseProblem(v) ?? DOSE_FORM_MESSAGE }),
    ),
  /**
   * The dosing interval, e.g. "en gång i veckan". TES-1375 (Ö-419): once a
   * week only. Every order that becomes or changes the patient's ordination
   * goes through this schema (the decision, the transfer approval and plan,
   * renewal, dose adjustment, the status decision), so a daily or irregular
   * interval is refused with a 400 before anything is stored.
   */
  frequency: weeklyFrequencySchema,
  // TES-956 (TES-941 §JOURNALANTECKNINGEN):
  // "Läkemedel, dos, frekvens och behandlingslängd ska vara strukturerade uppgifter"
  // Free text, e.g.
  // "i 4 veckor" or "tills vidare", because a titration step and an open-ended
  // order are both real answers and neither is a number of weeks.
  //
  // OPTIONAL, and it is ordination text ONLY: it never moves the 28-day cycle
  // (`cycleDurationDays`, `expiresAt`), which renewal and billing run on.
  // Absent = the caller says nothing about it (an older client; a stored
  // length is kept where there is one). Blank = "not stated", stored as null.
  duration: z.string().trim().max(64).optional(),
  // TES-1292 — the catalogue entry the parts were picked from, and the
  // motivation for a deviation. Optional: a free-text order is still valid.
  ...orderCatalogueFields,
});

// TES-736 — POST employee-api/v1/patients/:id/approve. The order plus the
// doctor's explicit, audited choice to activate the treatment while CP-06
// still has open items (Emelie 2026-09-16: "Läkaren måste uttryckligen"
// "bekräfta att behandlingen aktiveras med uppföljning kvar"). Without it the
// server refuses with 409 follow_up_remaining and lists what is open.
//
// TES-841 — and the clinical phase, REQUIRED and without a default. This is
// the post-visit half of Emelie's "På båda adminytorna – efter vårdmöte"
// "och vid asynkron F-07-granskning – måste Kirill välja klinisk"
// "behandlingsfas innan ett positivt behandlingsbeslut kan publiceras."
// The transfer surface has carried it since TES-734; this one had no phase
// choice at all, which is why the requirement lives in the schema rather than
// only in the admin form: an omitted phase must fail the request, not fall
// through to a route the doctor did not pick. `.optional()` here would BE the
// default she rules out.
//
// TES-1071 (TES-1069 S2, point 3) — and NO prescription choice. The
// approval plans the order; it no longer issues anything. TES-997 required a
// choice here because approving activated the treatment; since TES-1069 the
// prescription is decided only in "Bekräfta recept och publicera behandlingen",
// after a verified subscription (confirmPrescriptionSchema). Her words:
// "Admin får inte erbjuda eller förvälja ”Nytt recept är utfärdat i Webdoc” i detta steg."
// A request that still carries one is refused, never silently stripped: a
// client that believes it issued a prescription must be told it did not.
export const approveOnboardingBodySchema = prescriptionOrderSchema.extend({
  clinicalPhase: clinicalPhaseSchema,
  withFollowUpRemaining: z.boolean().optional(),
  prescription: z.never({ message: PRESCRIPTION_NOT_AT_DECISION }).optional(),
});
export type ApproveOnboardingBody = z.infer<typeof approveOnboardingBodySchema>;

/**
 * Is this a row from before TES-636, where `dose` carried the whole label?
 *
 * Such rows have neither `medication` nor `frequency`, and their dose looks
 * like "Testpreparat 0,25 mg/vecka" — it would never pass DOSE_FORM. That does
 * not matter for display (the label falls back to the whole string) but it
 * matters for PRE-FILLING: the renewal panel must not fill in a value the form
 * then refuses to send. The renewal would be blocked by a line the doctor did
 * not write and cannot correct without understanding why.
 */
export function isLegacyDoseLabel(cycle: {
  medication: string | null;
  dose: string | null;
}): boolean {
  return (
    !cycle.medication && !!cycle.dose && !DOSE_FORM.test(cycle.dose.trim())
  );
}
export type PrescriptionOrder = z.infer<typeof prescriptionOrderSchema>;

// The patient's label, composed from the three parts.
//
// It lives in the contract rather than in the app or in admin, because all
// three have to agree on how a prescription READS. `TreatmentScreen` renders
// this string verbatim as the prescription card's heading.
//
// Fallback for rows written before TES-636: they have neither `medication` nor
// `frequency`, only the old free-text dose. Then the dose IS the whole label —
// otherwise the history would have started showing " · null" for every earlier
// cycle.
// The keys are REQUIRED but the values nullable, deliberately: `medication?:`
// would have let a caller omit the field entirely, and the label then degrades
// silently to the strength alone. That happened immediately — `/me` had a
// `select` without the new columns, so the home screen showed "0,25 mg" while
// the prescription view showed the whole order. With required keys the same
// slip becomes a compile error.
//
// TES-956 — `duration` follows the same rule: a required key, so a `select`
// that forgets the new column is a compile error rather than a label that
// quietly stops showing the treatment length.
export function prescriptionLabel(cycle: {
  medication: string | null;
  dose: string;
  frequency: string | null;
  duration: string | null;
}): string {
  const head = [cycle.medication?.trim(), cycle.dose?.trim()]
    .filter(Boolean)
    .join(" ");
  const tail = [cycle.frequency?.trim(), durationPhrase(cycle.duration)]
    .filter(Boolean)
    .join(" ");
  return tail ? `${head} · ${tail}` : head;
}

/**
 * TES-1157 — medication and strength only ("Mounjaro 2,5 mg"), for /me's
 * `currentCycle.dose`. Every app build derives the Home badge from that field
 * by stripping everything but digits, commas and periods, so the full label's
 * frequency and duration figures leaked into it ("2,5" + "4 veckor" read
 * "2,54"). The full ordination stays on the prescription view.
 */
export function prescriptionHead(cycle: {
  medication: string | null;
  dose: string;
}): string {
  return [cycle.medication?.trim(), cycle.dose?.trim()]
    .filter(Boolean)
    .join(" ");
}

/**
 * TES-1159 — the ordination's parts for Home's "Din behandling" card, or
 * null when they cannot all be shown as written: a row from before TES-636
 * (no medication or frequency), or a strength the contract would refuse
 * today. "If fields disagree, show nothing derived" (TES-1159): the parts are
 * shown together or not at all.
 */
export function ordinationParts(cycle: {
  medication: string | null;
  dose: string;
  frequency: string | null;
}): { medication: string; strength: string; frequency: string } | null {
  const medication = cycle.medication?.trim() ?? "";
  const strength = cycle.dose?.trim() ?? "";
  const frequency = cycle.frequency?.trim() ?? "";
  if (!medication || !frequency || doseProblem(strength) !== null) return null;
  return { medication, strength, frequency };
}

// TES-1159 / TES-1375 — the once-a-week check lives in ./dose-interval, which
// the medication catalogue's schema reads too.
export {
  DOSE_INTERVAL_COPY,
  isOnceAWeek,
  WEEKLY_FREQUENCY,
  weeklyFrequencySchema,
} from "./dose-interval";

/**
 * The next planned dose date on a once-a-week regimen: the first dose date
 * plus whole weeks, the first such date on or after `today` (both
 * YYYY-MM-DD, tenant calendar). Pure date arithmetic.
 */
export function nextWeeklyDoseOn(firstDoseDate: string, today: string): string {
  const [fy, fm, fd] = firstDoseDate.split("-").map(Number);
  const [ty, tm, td] = today.split("-").map(Number);
  const first = Date.UTC(fy, fm - 1, fd);
  const days = Math.round((Date.UTC(ty, tm - 1, td) - first) / 86_400_000);
  const weeks = days <= 0 ? 0 : Math.ceil(days / 7);
  return new Date(first + weeks * 7 * 86_400_000).toISOString().slice(0, 10);
}

/**
 * The treatment length as it reads inside an ordination, or "" when none is
 * stated. The value is stored exactly as written; the one thing added here is
 * the preposition when the doctor wrote a bare figure, so "4 veckor" and
 * "i 4 veckor" both read "… en gång i veckan i 4 veckor". Anything that does
 * not start with a figure ("tills vidare", "under 3 månader") is left alone.
 */
export function durationPhrase(duration: string | null | undefined): string {
  const d = (duration ?? "").trim();
  if (!d) return "";
  return /^\d/.test(d) ? `i ${d}` : d;
}

/**
 * TES-956 — the order as one journal line:
 * "Ordination: Wegovy 0,25 mg · en gång i veckan i 4 veckor"
 * Built on `prescriptionLabel`, so the journal
 * says exactly what the patient reads in the app. Null when there is nothing
 * to state (no medication and no dose).
 */
export function ordinationLine(order: {
  medication: string | null;
  dose: string;
  frequency: string | null;
  duration: string | null;
}): string | null {
  const label = prescriptionLabel(order);
  if (!order.medication?.trim() && !order.dose?.trim()) return null;
  return `Ordination: ${label}`;
}

// Swedish number words for the small counts a treatment length is written in.
// Every unit the duration field takes for days, weeks and months is common
// gender, so one is "en"; the unit for years is neuter and takes "ett".
const COUNT_WORDS = [
  "",
  "en",
  "två",
  "tre",
  "fyra",
  "fem",
  "sex",
  "sju",
  "åtta",
  "nio",
  "tio",
  "elva",
  "tolv",
];

/**
 * A duration phrase with its count written out: "i 4 veckor" becomes
 * "i fyra veckor", "under 3 månader" becomes "under tre månader". Only a
 * whole number from one to twelve that stands before a word is written out;
 * larger counts stay as digits ("i 16 veckor"), and anything else is left as
 * written.
 */
export function durationProse(duration: string | null | undefined): string {
  return durationPhrase(duration).replace(
    /(^|\s)(\d{1,2})(?=\s+(\p{L}+))/gu,
    (whole, lead: string, digits: string, unit: string) => {
      const n = Number(digits);
      if (n < 1 || n > 12) return whole;
      const word = n === 1 && /^år$/i.test(unit) ? "ett" : COUNT_WORDS[n];
      return `${lead}${word}`;
    },
  );
}

/**
 * TES-1019 (Emelie, point 5) — the order as journal prose, for the note's
 * "Ordination" heading:
 * "Ordination: Wegovy 0,25 mg en gång i veckan i fyra veckor."
 * One sentence built from the structured Recept fields (medication, dose,
 * frequency, length), so Kirill never writes the same order twice. Unlike
 * `ordinationLine` (the patient's label, with its middle dot) it has no
 * separator, writes small counts as words and ends with a period. Null when
 * there is nothing to state (no medication and no dose).
 */
export function ordinationProse(order: {
  medication: string | null;
  dose: string;
  frequency: string | null;
  duration: string | null;
  strength?: string | null;
}): string | null {
  const body = ordinationProseBody(order);
  return body === null ? null : `Ordination: ${body}.`;
}

/**
 * The order as prose without the "Ordination:" lead or the full stop
 * ("Wegovy 0,25 mg en gång i veckan i fyra veckor"), for a sentence that
 * names a planned treatment rather than an ordination (TES-1185).
 */
export function ordinationProseBody(order: {
  medication: string | null;
  dose: string;
  frequency: string | null;
  duration: string | null;
  // TES-1185 — "styrka", after the medication. Proposed wording.
  strength?: string | null;
}): string | null {
  if (!order.medication?.trim() && !order.dose?.trim()) return null;
  return [
    order.medication?.trim(),
    order.strength?.trim() ? `(styrka ${order.strength.trim()})` : null,
    order.dose?.trim(),
    order.frequency?.trim(),
    durationProse(order.duration),
  ]
    .filter(Boolean)
    .join(" ")
    .replace(/[.\s]+$/, "");
}

// TES-956 — the order Kirill proposes AT THE MEETING, before any decision.
// Four strings, "" = not written yet: a draft may be half-done when the
// meeting is documented, and refusing to close the meeting over a missing
// frequency would push the order back into a free-text box. The complete,
// validated order is required only where it becomes the patient's
// prescription — the approval (Ö-247: no cycle before approval).
//
// Stored field-encrypted under the patient's key on the meeting row, like the
// free-text `Meeting.prescription` it replaces.
export const meetingPrescriptionOrderSchema = z.object({
  medication: z.string().trim().max(120),
  dose: z.string().trim().max(64),
  frequency: z.string().trim().max(64),
  duration: z.string().trim().max(64),
  // TES-1185 (TES-1180 point 1) — the two parts of Emelie's list the
  // meeting did not ask for: "styrka" and "behandlingsplan". Optional on the
  // wire so an older admin bundle and every reader of the four keep working.
  strength: z.string().trim().max(64).optional(),
  plan: z.string().trim().max(2000).optional(),
  // TES-1292 — as on prescriptionOrderSchema.
  ...orderCatalogueFields,
});
export type MeetingPrescriptionOrder = z.infer<
  typeof meetingPrescriptionOrderSchema
>;

/** True when not one of the four parts is written. */
export function meetingOrderIsEmpty(
  o: MeetingPrescriptionOrder | null | undefined,
): boolean {
  return (
    !o ||
    !(
      o.medication.trim() ||
      o.dose.trim() ||
      o.frequency.trim() ||
      o.duration.trim() ||
      o.strength?.trim() ||
      o.plan?.trim()
    )
  );
}

export const prescriptionCycleSchema = z.object({
  id: z.string().uuid(),
  treatmentId: z.string().uuid(),
  cycleNumber: z.number().int().positive(),
  dose: z.string(),
  prescribedByDoctorId: z.string().uuid().nullable(),
  prescribedAt: z.date().nullable(),
  expiresAt: z.date().nullable(),
  state: prescriptionCycleStateSchema,
  stateChangedAt: z.date(),
  webdocPrescriptionRef: z.string().nullable(),
  previousCycleId: z.string().uuid().nullable(),
});
export type PrescriptionCycle = z.infer<typeof prescriptionCycleSchema>;

// CAR-18 — request body for the patient renewal check-in. Posted to
// `/patient-api/v1/cycles/:id/renew`. The API parses this schema at the
// boundary (TES-649); it is also the wire type the mobile client builds
// against. The weight must be positive, as the app's own form requires.
// `sideEffects` may be omitted: the route always read a missing one as null,
// and the app sends null when the box is empty.
//
// TES-1443 — `symptoms`: the register symptoms she chose after the free
// text, with their details and safety answers (renewalSymptomsSchema).
// Optional and additive: 2.0.11 sends none and is served exactly as before.
export const renewalPayloadSchema = z.object({
  currentWeightKg: z.number().positive(),
  sideEffects: z.string().nullable().optional(),
  doingWell: z.enum(["yes", "partly", "no"]),
  questionsForDoctor: z.string().optional(),
  symptoms: renewalSymptomsSchema.optional(),
});
export type RenewalPayload = z.infer<typeof renewalPayloadSchema>;

/**
 * The renewal's answer. `symptomSafety` only when she sent symptoms: the
 * strongest safety outcome among them, so the app shows the same safety
 * copy or acute path as after a direct registration (Ö-447). A request
 * without symptoms (every 2.0.11 request) gets `{ ok: true }` as before.
 */
export interface RenewalSubmissionResult {
  ok: true;
  symptomSafety?: SymptomSafetyLevel;
}

// TES-691 step 2 — pre-fill the prescription from the consult note.
//
// The cycle is 28 days (cycleConfig), so a note normally describes ONE period
// and "i 4 veckor" is the cycle's own length rather than new information.
//
// Does not touch Ö-26: that governs the patient's prescription view. What is
// pre-filled here are the doctor's own fields, which she reads, edits and
// signs — the patient only ever sees what she confirmed.
//
// Conservative on purpose: if no strength is found, nothing is filled in, and
// uncertain parts are left empty rather than guessed.
// Negative lookahead on "/": "6 mg/ml" is a CONCENTRATION, not a dose. Without
// it, "Victoza 6 mg/ml, 0,6 mg dagligen" was filled in as 6 mg — the strength
// per millilitre presented as the patient's dose. It is skipped now, so the
// line above yields 0,6 mg, and a line containing ONLY a concentration
// pre-fills nothing at all (which is right: it contains no dose).
const DOSE_RE = /(\d+(?:[.,]\d+)?)\s*(mg|mikrogram|µg|ug|ml|IE|E)\b(?!\s*\/)/i;
const DOSE_RE_G = new RegExp(DOSE_RE.source, "gi");

// The route of administration is not part of the dosing interval.
const ROUTE_RE =
  /\b(s\.?\s?c\.?|subkutan[t]?|i\.?\s?m\.?|intramuskul[äa]r[t]?|p\.?\s?o\.?|per\s+os|inj(?:ektion)?)\b/gi;

// "i 4 veckor" / "under 4 veckor" — treatment duration. TES-956 gave it a
// field of its own ("Behandlingslängd"), so it is lifted out of the interval
// and into that field instead of being dropped. It is ordination text only;
// the 28-day cycle stays the cycle's.
const DURATION_RE =
  /\b(i|under)\s+\d+\s*(dag(?:ar)?|veck(?:a|or)|m[åa]nad(?:er)?)\b/gi;

// The titration tail: "…, sedan 0,5 mg". It belongs to a later cycle
// (adjustDose with reason "titration"), not to the prescription being set now.
//
// NOTE: no leading \b. JavaScript's \b is ASCII-based, so it NEVER matches
// before "ö" — "därefter" was caught but "öka till" slipped through silently.
// The start-or-separator requirement is therefore spelled out.
const TITRATION_TAIL_RE =
  /(?:^|[\s,;])(?:sedan|d[äa]refter|[öo]ka\s+till)\b[\s\S]*$/i;

export interface ParsedPrescriptionNote {
  /** The parts we dare fill in. A missing part is empty, never guessed. */
  order: Partial<PrescriptionOrder>;
  /** The note mentions more than one strength — the doctor must look. */
  multipleDoses: boolean;
}

export function parsePrescriptionNote(
  note: string | null | undefined,
): ParsedPrescriptionNote {
  const empty: ParsedPrescriptionNote = { order: {}, multipleDoses: false };
  const text = (note ?? "").trim();
  if (!text) return empty;

  const doses = text.match(DOSE_RE_G) ?? [];
  const m = DOSE_RE.exec(text);
  // No strength at all → no pre-fill. Guessing the medication without a dose
  // would be filling in half the prescription on a hunch.
  if (!m || m.index === undefined) return empty;

  const multipleDoses = doses.length > 1;

  // The dose is normalised in whitespace only — the number and the unit stay
  // exactly as the doctor wrote them, decimal comma and all.
  const dose = `${m[1]} ${m[2]}`;

  const medication = text
    .slice(0, m.index)
    .replace(/[,;:–-]+\s*$/, "")
    .trim();

  const afterDose = text
    .slice(m.index + m[0].length)
    .replace(TITRATION_TAIL_RE, "");
  // The first stated length only; a second one belongs to the titration tail
  // that was cut above.
  const duration = afterDose.match(DURATION_RE)?.[0]?.trim();

  const frequency = afterDose
    .replace(DURATION_RE, "")
    .replace(ROUTE_RE, "")
    .replace(/\s+/g, " ")
    .replace(/^[\s,;:.–-]+/, "")
    .replace(/[\s,;:.–-]+$/, "")
    .trim();

  const order: Partial<PrescriptionOrder> = { dose };
  if (medication) order.medication = medication;
  if (frequency) order.frequency = frequency;
  if (duration) order.duration = duration;
  return { order, multipleDoses };
}
